import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation, useNavigate } from 'react-router-dom';
import 'reactflow/dist/style.css';
import './App.css';
import './Builder.css';

// Auth & Layout
import { AuthProvider, useAuth } from './context/AuthContext';
import { canUseDesigner } from './utils/tierEntitlements';
import { SessionProvider, useSession } from './context/SessionContext';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import { Layout } from './components/Layout';
import { ProtectedRoute, PublicRoute } from './components/ProtectedRoute';

// Pages
import { LoginPage } from './pages/auth';
import { ThreediumCallbackPage } from './pages/auth/ThreediumCallbackPage';
import { AdminDashboard, AdminUsers, AdminProjectsPage, AdminTierLimits, AdminModels, AdminUsage } from './pages/admin';
import { ProfilePage } from './pages/profile';
import { LandingPage } from './pages/landing';
import { DesignerPage } from './pages/designer';
import { UsagePage } from './pages/usage';

// Custom components
import Header from './components/Header';
import ErrorBoundary from './components/ErrorBoundary';
import { BuilderPage } from './components/builder';
import { AgentDesigner } from './components/designer';
import SessionBootScreen from './components/builder/SessionBootScreen';
import { useAgentConfigStore } from './stores/agentConfigStore';
import { useProjectStore } from './stores/projectStore';
import { PublishModal } from './components/PublishModal';
import { consumeThreediumDeepLink } from './utils/threediumDeepLink';

/**
 * Main App Content (Builder + Designer)
 * 
 * This is the integrated view where Builder and Designer work together.
 * Users can switch between modes while working on a project.
 * 
 * The AgentDesigner component is the SINGLE SOURCE OF TRUTH for the designer UI.
 * It's used both here (embedded) and in the standalone DesignerPage.
 */
function MainApp({ initialMode = 'builder', projectId: propProjectId }) {
  // Get project ID from URL params (for /experience/:projectId route)
  const { projectId: urlProjectId } = useParams();
  const projectIdToLoad = propProjectId || urlProjectId;
  const navigate = useNavigate();

  // Session readiness — AgentDesigner talks to the session backend, so defer mount
  const { isReady: sessionReady, isCloudMode, sessionStatus, error: sessionError, retrySession } = useSession();
  const { user } = useAuth();
  const designerAllowed = canUseDesigner(user);

  // Initialize agent config store
  const { initialize, initialized } = useAgentConfigStore();

  useEffect(() => {
    if (!initialized) {
      initialize();
    }
  }, [initialize, initialized]);

  // Mode state: 'builder' or 'agent-designer'
  const [activeMode, setActiveMode] = useState(initialMode === 'agent-designer' && !designerAllowed ? 'builder' : initialMode);

  useEffect(() => {
    if (!designerAllowed && activeMode === 'agent-designer') {
      setActiveMode('builder');
    }
  }, [designerAllowed, activeMode]);
  // Builder state for header visibility ('start', 'prompt', 'workspace')
  const [builderState, setBuilderState] = useState('start');
  // Use shared theme context
  const { theme, setTheme } = useTheme();
  // Builder chat sidebar visibility
  const [builderChatSidebarOpen, setBuilderChatSidebarOpen] = useState(true);
  // Builder view mode: 'preview' or 'code'
  const [builderViewMode, setBuilderViewMode] = useState('preview');
  const [deviceMode, setDeviceMode] = useState('desktop');
  
  // Publish modal state
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [currentProject, setCurrentProject] = useState(null);
  
  // Handle publish button click
  const handlePublishClick = useCallback(() => {
    if (currentProject) {
      setShowPublishModal(true);
    }
  }, [currentProject]);
  
  // Handle project info updates from BuilderPage
  const handleProjectChange = useCallback((project) => {
    setCurrentProject(project);
  }, []);

  // List vs project (and A vs B) remount via MainAppGate. These effects only
  // clean leftover store/handoff state — they do not drive the visible screen.
  useEffect(() => {
    if (projectIdToLoad) {
      return () => {
        consumeThreediumDeepLink();
      };
    }
    const store = useProjectStore.getState();
    store.stopAllVite();
    useProjectStore.setState({ currentProject: null });
    return undefined;
  }, [projectIdToLoad]);

  const handleBackToDashboard = useCallback(async () => {
    consumeThreediumDeepLink();
    setActiveMode('builder');
    try {
      await useProjectStore.getState().stopAllVite();
    } catch {
      // Still leave even if Vite stop fails
    }
    navigate('/dashboard');
  }, [navigate]);

  return (
    <div className="App main-app">
      <Header
        activeMode={activeMode}
        onModeChange={(mode) => {
          if (mode === 'agent-designer' && !designerAllowed) return;
          setActiveMode(mode);
        }}
        showLeftRibbon={builderState === 'workspace' || activeMode === 'agent-designer'}
        theme={theme}
        onThemeChange={setTheme}
        chatSidebarOpen={builderChatSidebarOpen}
        onToggleChatSidebar={() => setBuilderChatSidebarOpen(prev => !prev)}
        viewMode={builderViewMode}
        onViewModeChange={setBuilderViewMode}
        deviceMode={deviceMode}
        onDeviceModeChange={setDeviceMode}
        onPublishClick={handlePublishClick}
        project={currentProject}
        onBackToDashboard={handleBackToDashboard}
      />

      {/* Builder Mode */}
      <div
        className="mode-container builder-mode"
        style={{ display: activeMode === 'builder' ? 'block' : 'none' }}
      >
        <BuilderPage
          onStateChange={setBuilderState}
          chatSidebarOpen={builderChatSidebarOpen}
          viewMode={builderViewMode}
          deviceMode={deviceMode}
          initialProjectId={projectIdToLoad}
          onProjectChange={handleProjectChange}
        />
      </div>

      {/* Agent Designer Mode - wait for session in cloud (same gate as builder) */}
      {activeMode === 'agent-designer' && (
        <div className="mode-container agent-designer-mode" style={{ display: 'contents' }}>
          {isCloudMode && !sessionReady ? (
            <SessionBootScreen
              sessionStatus={sessionStatus}
              error={sessionError}
              onRetry={retrySession}
            />
          ) : (
            <ErrorBoundary name="Agent Designer">
              <AgentDesigner
                standalone={true}
                visible={true}
              />
            </ErrorBoundary>
          )}
        </div>
      )}
      
      {/* Publish Modal */}
      <PublishModal
        projectId={currentProject?.aws_project_id || currentProject?.id}
        projectName={currentProject?.display_name || currentProject?.name}
        isOpen={showPublishModal}
        onClose={() => setShowPublishModal(false)}
        initialData={{
          subdomain: currentProject?.subdomain,
          visibility: currentProject?.visibility,
          is_published: currentProject?.is_published,
          preview_url: currentProject?.preview_url
        }}
      />
    </div>
  );
}

// Same component on /dashboard and /experience/:id. A key makes the URL the
// place: list vs project vs another project remounts instead of reusing state.
function MainAppGate() {
  const { projectId } = useParams();
  return <MainApp key={projectId ?? 'dashboard'} />;
}

// Root Route - Shows LandingPage for unauthenticated users, redirects to /dashboard for authenticated
function RootRoute() {
  const { isAuthenticated, isLoading } = useAuth();

  // Show loading spinner while checking auth
  if (isLoading) {
    return (
      <div className="auth-loading">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  // Show landing page for unauthenticated users
  if (!isAuthenticated) {
    return <LandingPage />;
  }

  // Redirect authenticated users to /dashboard
  return <Navigate to="/dashboard" replace />;
}

function CatchAllRedirect() {
  const { isAuthenticated, isLoading } = useAuth();
  if (isLoading) {
    return (
      <div className="auth-loading">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }
  return <Navigate to={isAuthenticated ? '/dashboard' : '/'} replace />;
}

// Global unhandled rejection handler (C-05) + source map suppression (H-22)
function GlobalRejectionHandler({ children }) {
  useEffect(() => {
    const handler = (event) => {
      const reason = event?.reason;
      const msg = reason?.message ?? (typeof reason === 'string' ? reason : String(reason ?? ''));
      if (msg.includes('.map') || msg.toLowerCase().includes('sourcemap')) return;
      console.error('Unhandled promise rejection:', reason);
    };
    window.addEventListener('unhandledrejection', handler);
    return () => window.removeEventListener('unhandledrejection', handler);
  }, []);
  return children;
}

// Routes wrapped in ErrorBoundary with pathname reset (C-02)
function AppRoutes() {
  const location = useLocation();
  return (
    <ErrorBoundary name="App" resetKey={location.pathname}>
      <Routes>
            {/* Public Auth Routes */}
            <Route path="/login" element={
              <PublicRoute>
                <LoginPage />
              </PublicRoute>
            } />
            {/* Threedium OAuth callback */}
            <Route path="/auth/threedium/callback" element={
              <ThreediumCallbackPage />
            } />

            {/* Protected Routes with Layout */}
            <Route path="/profile" element={
              <ProtectedRoute>
                <Layout pageTitle="Profile & Settings">
                  <ProfilePage />
                </Layout>
              </ProtectedRoute>
            } />
            
            {/* Settings - alias for profile */}
            <Route path="/settings" element={
              <ProtectedRoute>
                <Layout pageTitle="Settings">
                  <ProfilePage />
                </Layout>
              </ProtectedRoute>
            } />

            {/* Usage Analytics */}
            <Route path="/usage" element={
              <ProtectedRoute>
                <Layout pageTitle="Usage Analytics">
                  <UsagePage />
                </Layout>
              </ProtectedRoute>
            } />

            {/* Admin Routes */}
            <Route path="/admin" element={
              <ProtectedRoute requireAdmin>
                <Layout pageTitle="Admin Dashboard">
                  <AdminDashboard />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/admin/users" element={
              <ProtectedRoute requireAdmin>
                <Layout pageTitle="User Management">
                  <AdminUsers />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/admin/projects" element={
              <ProtectedRoute requireAdmin>
                <Layout pageTitle="All Projects">
                  <AdminProjectsPage />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/admin/tier-limits" element={
              <ProtectedRoute requireSuperAdmin>
                <Layout pageTitle="Tier Limits">
                  <AdminTierLimits />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/admin/models" element={
              <ProtectedRoute requireSuperAdmin>
                <Layout pageTitle="Model Management">
                  <AdminModels />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/admin/usage" element={
              <ProtectedRoute requireSuperAdmin>
                <Layout pageTitle="User Spend">
                  <AdminUsage />
                </Layout>
              </ProtectedRoute>
            } />

            {/* Dashboard - explicit route for the main builder */}
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <MainAppGate />
              </ProtectedRoute>
            } />

            {/* Experience/Project - opens specific project in builder */}
            <Route path="/experience/:projectId" element={
              <ProtectedRoute>
                <MainAppGate />
              </ProtectedRoute>
            } />

            {/* Agent Designer - standalone page for creating/managing workflow presets */}
            <Route path="/designer" element={
              <ProtectedRoute requireDesigner>
                <DesignerPage />
              </ProtectedRoute>
            } />

            {/* Landing Page for unauthenticated users, redirects to /dashboard for authenticated */}
            <Route path="/" element={<RootRoute />} />

            {/* Fallback - landing for guests, dashboard for signed-in users */}
            <Route path="*" element={<CatchAllRedirect />} />
      </Routes>
    </ErrorBoundary>
  );
}

// Root App with Router
function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <SessionProvider>
            <GlobalRejectionHandler>
              <AppRoutes />
            </GlobalRejectionHandler>
          </SessionProvider>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
